import { boolFromUnknown, intFromUnknown, strFromUnknown } from '@/lib/primitive-utils';
import { LabeledCheckboxRow, LabeledSelectRow, LabeledTextRow } from './shared/field-primitives';
import { WidthField } from './shared/tolerance-width-fields';
import type { BuilderPropertyRecord, ShortAnswerToolSettingsProps } from './tool-defaults';

const CORRECTNESS_LABELS = ['Truth table', 'Truth table. Exact match', 'Exact match', 'Exact match for quantifiers'];

function sameItemsInCommaSeparatedList(...strings: string[]) {
    const [list1, list2] = strings.map(string => string.split(','));
    return list1.length === list2.length && list1.every(item => list2.includes(item));
}

function correctnessMethodsUiLabel(methods: unknown): string {
    const s = String(methods ?? '');
    if (s === 'truth table') {
        return 'Truth table';
    }
    if (sameItemsInCommaSeparatedList(s, 'truth table,exact match ignoring parens')) {
        return 'Truth table. Exact match';
    }
    if (s === 'exact match ignoring parens') {
        return 'Exact match';
    }
    if (sameItemsInCommaSeparatedList(s, 'exact match ignoring parens,quantifiers reorderable')) {
        return 'Exact match for quantifiers';
    }
    return 'Truth table. Exact match';
}

function patchForCorrectnessUiLabel(label: string): BuilderPropertyRecord {
    switch (label) {
        case 'Truth table':
            return { correctness_methods: 'truth table' };
        case 'Truth table. Exact match':
            return { correctness_methods: 'truth table,exact match ignoring parens' };
        case 'Exact match':
            return { correctness_methods: 'exact match ignoring parens' };
        case 'Exact match for quantifiers':
            return { correctness_methods: 'exact match ignoring parens,quantifiers reorderable' };
        default:
            return {};
    }
}

export function selectPropositionalLogicToolProps(p: BuilderPropertyRecord) {
    return {
        boolean_variables: strFromUnknown(p.boolean_variables, ''),
        untyped_variables: strFromUnknown(p.untyped_variables, ''),
        boolean_functions: strFromUnknown(p.boolean_functions, ''),
        support_true_and_false: boolFromUnknown(p.support_true_and_false),
        operators: strFromUnknown(p.operators, ''),
        correctnessUiLabel: correctnessMethodsUiLabel(p.correctness_methods),
        editor_atomic_symbols: strFromUnknown(p.editor_atomic_symbols, ''),
        width: intFromUnknown(p.width, 150),
    };
}

function PropositionalLogicToolSettingsInner(
    props: ReturnType<typeof selectPropositionalLogicToolProps> & {
        onPatch: (patch: BuilderPropertyRecord) => void;
    },
) {
    const {
        boolean_variables,
        untyped_variables,
        boolean_functions,
        support_true_and_false,
        operators,
        correctnessUiLabel,
        editor_atomic_symbols,
        width,
        onPatch,
    } = props;

    return (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-2 sm:gap-x-3 sm:gap-y-2">
            <LabeledTextRow
                label="Boolean variables"
                description="Ex: p,q,r"
                value={boolean_variables}
                rows={2}
                onValueChange={v => onPatch({ boolean_variables: v })}
            />
            <LabeledTextRow
                label="Untyped variables"
                description="Ex: x,y,z"
                value={untyped_variables}
                rows={2}
                onValueChange={v => onPatch({ untyped_variables: v })}
            />
            <LabeledTextRow
                label="Boolean functions"
                description="Ex: P,Q,R"
                value={boolean_functions}
                rows={2}
                onValueChange={v => onPatch({ boolean_functions: v })}
            />
            <LabeledCheckboxRow
                label="Support True and False"
                checked={support_true_and_false}
                onCheckedChange={v => onPatch({ support_true_and_false: v })}
            />
            <LabeledTextRow
                label="Operators"
                description="Ex: \land,\lor,\lnot"
                value={operators}
                rows={2}
                onValueChange={v => onPatch({ operators: v })}
            />
            <LabeledSelectRow
                label="Correctness methods"
                value={correctnessUiLabel}
                options={CORRECTNESS_LABELS}
                onValueChange={v => onPatch(patchForCorrectnessUiLabel(v))}
            />
            <LabeledTextRow
                label="Editor custom keys"
                description='Key and latex pairs. Ex: [{"key":"P","latex":"P(x)"}]'
                value={editor_atomic_symbols}
                rows={2}
                monospace
                onValueChange={v => onPatch({ editor_atomic_symbols: v })}
            />
            <WidthField
                width={width}
                onPatch={onPatch}
            />
        </div>
    );
}

export function PropositionalLogicToolSettings({ builderProps, onPatch }: ShortAnswerToolSettingsProps) {
    return (
        <PropositionalLogicToolSettingsInner
            {...selectPropositionalLogicToolProps(builderProps)}
            onPatch={onPatch}
        />
    );
}
